/**
 * competitorMap.js — every RotoGears service tied to its competitor equivalents
 *
 * Drives the quote builder's Competition analysis. Keyed by the RotoGears id a
 * quote line carries (`line.productId`), which both price lists share, so one
 * entry covers an item on the New and the Old list alike. Old-only ids are
 * listed too, so a reopened Old quote still compares.
 *
 * Each competitor slot is one of:
 * - `{ items: [...], note? }`: what a buyer would tick on that competitor's
 *   form for the same deliverable. An item is a product id, or
 *   `{ id, perLine: true }` to multiply by the line's quantity where their
 *   product isn't per-image in their own catalog (an extra reel each).
 * - `{ includedIn: id, note }`: only comes inside that product of theirs. It
 *   compares as included when that product is on the comparison, else as not
 *   offered (Framed by JG's aerials, which ship with their Photography).
 * - `{ items, includedWith: [ids] }`: priced from `items`, unless one of those
 *   products of theirs is on the comparison and already includes it
 *   (VirTour's Cinematic and Luxury videos ship a branded version).
 * - `null` with `none`: they sell nothing comparable. `none` says why.
 *
 * `flashRef` (photos only): our Flash Photos product of the same scope, priced
 * beside the row, because VirTour's stills are a flash shoot.
 *
 * Matching is by deliverable, not by name. Notes carry the judgment calls, and
 * the modal shows them on the row, so a mismatch is never hidden in a total.
 * Competitor prices live in virtourCatalog.js and framedByJgCatalog.js; this
 * file holds no prices.
 */

const JG_NO_FPV = 'They sell no FPV flight.';
const VT_FPV_BOTH = 'Theirs ships a real-time wide cut and a speed-ramped vertical reel.';
const VT_AGENT = 'They mic you up for an intro and outro, as a separate branded version.';
const JG_NO_TOURS = 'No Matterport on their form; their 3D tour is Zillow 3D Home.';
const JG_D2N = 'Their $350 Day-to-Night is an add-on to their own video; they sell no FPV flight.';
const MW_FPV = 'Horizontal and vertical full tours plus two short reels.';
const MW_VIDEOS = ['mw_basic_video', 'mw_cinematic_film', 'mw_signature_film'];
const VT_PACKAGE_ONLY = 'Listed only inside their MLS+ package, so it may not be sold on its own.';

export const COMPETITOR_MAP = {
  // ── Photos ──
  photo_ie: {
    flashRef: 'photo_flash',
    vt: { items: ['vt_still_photography'], note: 'Theirs is a flash shoot.' },
    jg: { items: ['photography'], note: 'HDR, flash and ambient blended. Aerials included.' },
    mw: { items: ['mw_photo_hdr'], note: 'Their standard HDR look; aerials included.' },
  },
  photo_iea: {
    flashRef: 'photo_flash_a',
    vt: { items: ['vt_still_photography', 'vt_aerial_photography'], note: 'Theirs is a flash shoot.' },
    jg: { items: ['photography'], note: 'HDR, flash and ambient blended. Aerials included.' },
    mw: { items: ['mw_photo_hdr'], note: 'Their standard HDR look; aerials included.' },
  },
  photo_flash: {
    vt: { items: ['vt_still_photography'] },
    jg: { items: ['photography'], note: 'Aerials included.' },
    mw: { items: ['mw_photo_flash'], note: 'Flash and ambient, hand-blended; aerials included.' },
  },
  photo_flash_a: {
    vt: { items: ['vt_still_photography', 'vt_aerial_photography'] },
    jg: { items: ['photography'], note: 'Aerials included.' },
    mw: { items: ['mw_photo_flash'], note: 'Flash and ambient, hand-blended; aerials included.' },
  },
  photo_tw: {
    vt: { items: ['vt_twilight_photography'] },
    jg: { items: ['tw_photo'], note: 'An add-on to their Photography.' },
    mw: { items: ['mw_twilight_photos'] },
  },
  photo_twa: {
    vt: { items: ['vt_twilight_photography', 'vt_aerial_photography'] },
    jg: { items: ['tw_photo'], note: 'An add-on to their Photography, whose aerials are daytime; dusk aerials aren’t listed.' },
    mw: { items: ['mw_twilight_photos', 'mw_aerial_photos'], note: 'Dusk aerials only come in their Photography Bundle.' },
  },

  // ── Listing Video & Reels ──
  video_mls: {
    vt: { items: ['vt_highlight_video'], note: 'Their 1 minute Highlight video: aerials, grounds and the best rooms.' },
    jg: { items: ['basicvideo'], note: 'Their walkthrough video. Their cinematic film matches the Listing Film.' },
    mw: { items: ['mw_basic_video', 'mw_aerial_video'], note: 'Their basic video has no drone; aerial video is extra.' },
  },
  video_film: {
    vt: { items: ['vt_cinematic_video'], note: 'Their 1 to 2 minute Cinematic video.' },
    jg: { items: ['film'] },
    mw: { items: ['mw_cinematic_film'], note: '60 to 90s, agent on camera and scripting included.' },
  },
  video_combo: {
    vt: { items: ['vt_highlight_video', 'vt_vertical_reel'] },
    jg: { items: ['basicvideo', 'basicreel'] },
    mw: { items: ['mw_basic_video', 'mw_aerial_video', 'mw_basic_video'], note: 'Their basic video is a film OR a reel, so two of them.' },
  },
  video_reel: {
    vt: { items: ['vt_vertical_reel'] },
    jg: { items: ['basicreel'] },
    mw: { items: ['mw_basic_video'], note: 'Their basic option, as a reel.' },
  },
  reel_dynamic: {
    vt: { items: ['vt_vertical_reel'], note: 'No speed-ramped version on their form.' },
    jg: { items: ['basicreel'], note: 'Pacing is a free choice on their form.' },
    mw: { items: ['mw_basic_video'], note: 'Their basic reel has minimal speed ramps; full ones come with the cinematic.' },
  },
  reel_story: {
    vt: { items: ['vt_agent_guided_tour'], note: 'Agent-led, horizontal, for YouTube and websites.' },
    jg: { items: ['luxreel'], note: 'Agent on camera plus lifestyle.' },
    mw: { items: ['mw_cinematic_film'], note: 'Their cinematic reel: agent on camera, scripting, 3D captions.' },
  },
  reel_simple: {
    vt: { items: ['vt_vertical_reel'], note: 'Their cheapest reel sold on its own; their $174 vertical edit needs one of their listing videos.' },
    jg: { items: ['basicreel'], note: 'Their cheapest reel; their $200 vertical crop needs one of their videos.' },
    mw: { items: ['mw_one_take'], note: 'A one-take vertical walkthrough.' },
  },

  // ── RotoTour ──
  drone_mls: {
    vt: { items: ['vt_fpv_tour'], note: VT_FPV_BOTH },
    jg: null, none: { jg: JG_NO_FPV },
    mw: { items: ['mw_fpv_tour'], note: MW_FPV },
  },
  drone_reel: {
    vt: { items: ['vt_fpv_tour'], note: VT_FPV_BOTH },
    jg: null, none: { jg: JG_NO_FPV },
    mw: { items: ['mw_fpv_tour'], note: MW_FPV },
  },
  drone_story: {
    vt: { items: ['vt_fpv_tour'], note: 'No 3D titles on theirs.' },
    jg: null, none: { jg: JG_NO_FPV },
    mw: { items: ['mw_fpv_tour'], note: MW_FPV },
  },
  drone_bundle: {
    vt: { items: ['vt_fpv_tour'], note: VT_FPV_BOTH },
    jg: null, none: { jg: JG_NO_FPV },
    mw: { items: ['mw_fpv_tour'], note: MW_FPV },
  },

  // ── Floor Plans & 3D Tours ──
  fp_2d: {
    vt: { items: ['vt_floor_plan'], note: 'Their $100 plan is only sold with a Matterport scan.' },
    jg: { items: ['floorplan'] },
    mw: { items: ['mw_fp_2d'] },
  },
  fp_2d3d: {
    vt: { items: ['vt_floor_plan', 'vt_add_3d_floor_plan'] },
    jg: { items: ['floorplan'], note: 'No 3D plan on their form.' },
    mw: { items: ['mw_fp_2d', 'mw_fp_3d'] },
  },
  matterport: {
    vt: { items: ['vt_matterport'] },
    jg: null, none: { jg: JG_NO_TOURS },
    mw: { items: ['mw_matterport'] },
  },
  matterport_fp: {
    vt: { items: ['vt_matterport', 'vt_add_2d_floor_plan'] },
    jg: null, none: { jg: JG_NO_TOURS },
    mw: { items: ['mw_matterport', 'mw_fp_2d'] },
  },
  zillow: {
    vt: { items: ['vt_zillow_showcase'] },
    jg: { items: ['zillow'] },
    mw: { items: ['mw_zillow'] },
  },

  // ── Estate Film ──
  // The Estate Film includes every Listing Video option, so theirs carries the
  // extras they sell for the same things.
  film_sig: {
    vt: { items: ['vt_luxury_video', 'vt_fpv_clips', 'vt_agent_narration', 'vt_optimized_vertical_reel'],
      note: 'Their Luxury video plus FPV clips, narration and a vertical edit, which ours includes. Their Full Production Property Film is $9,500 flat, 4,000 sq ft and up.' },
    jg: { items: ['film', 'life_video', 'agent', 'vcrop'],
      note: 'Their top film plus lifestyle, agent on camera and a vertical crop, which ours includes. No multi-day production listed.' },
    mw: { items: ['mw_signature_film'], note: 'Full or two-day shoot; twilight, timelapse and agent on camera included.' },
  },

  // ── Aerial & Land ──
  aerial_home: {
    vt: { items: ['vt_aerial_photography'] },
    jg: { includedIn: 'photography', note: 'Comes with their Photography; not sold alone.' },
    mw: { items: ['mw_aerial_photos'], includedWith: ['mw_photo_hdr', 'mw_photo_flash'] },
  },
  aerial_lot_photo: {
    vt: { items: ['vt_aerial_photography'] },
    jg: null, none: { jg: 'They sell no land shoot.' },
    mw: { items: ['mw_aerial_photos'] },
  },
  aerial_lot_video: {
    vt: { items: ['vt_drone_video'] },
    jg: null, none: { jg: 'They sell no land shoot.' },
    mw: { items: ['mw_aerial_video'] },
  },
  aerial_lot_bundle: {
    vt: { items: ['vt_aerial_photography', 'vt_drone_video'] },
    jg: null, none: { jg: 'They sell no land shoot.' },
    mw: { items: ['mw_aerial_photos', 'mw_aerial_video'] },
  },

  // ── Photo add-ons ──
  ao_vtwilight: {
    vt: { items: ['vt_virtual_twilight'], note: 'A set of photos for one price, not per image.' },
    jg: { items: ['ai_twilight'], note: 'Their form doesn’t say per image.' },
    mw: { items: ['mw_virtual_twilight'] },
  },
  ao_vstaging: {
    vt: { items: ['vt_virtually_staged_image'], note: '$40 a photo from 4.' },
    jg: { items: ['vstaging'] },
    mw: { items: ['mw_virtual_staging'] },
  },
  ao_proplines: {
    vt: { items: ['vt_property_lines'] },
    jg: null, mw: null, none: { mw: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_beacons: {
    vt: { items: ['vt_location_markers'] },
    jg: null, mw: null, none: { mw: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_ig_carousel: {
    vt: null, jg: null, mw: null, none: { mw: 'Not on their form.', vt: 'Not on their form.', jg: 'Not on their form.' },
  },

  // ── Listing Video options ──
  ao_lv_fpv: {
    vt: { items: ['vt_fpv_clips'] },
    jg: null, mw: null, none: { mw: 'FPV only as their full $1,500 tour.', jg: JG_NO_FPV },
  },
  ao_lv_agent: {
    vt: { items: ['vt_agent_narration'], note: VT_AGENT },
    jg: { items: ['agent'] },
    mw: { items: ['mw_agent_script'], note: 'On camera, with a script and coaching.' },
  },
  ao_lv_ramps: {
    vt: null, jg: null, mw: null, none: { mw: 'Speed ramps come with their cinematic film.', vt: 'Not on their form.', jg: 'Pacing is a free choice on their form.' },
  },
  ao_timelapse: {
    vt: null, jg: null, mw: null, none: { mw: 'Timelapse only comes in their Signature film.', vt: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_lv_teaser: {
    vt: { items: ['vt_optimized_vertical_reel'], note: 'Their listing video reworked into a vertical edit.' },
    jg: { items: ['vcrop'] },
    mw: { items: ['mw_reel_booster'], note: 'Four social cuts from the shoot; not sold singly.' },
  },

  // ── Reel options ──
  ao_reel_agent: {
    vt: { items: ['vt_agent_narration'], note: VT_AGENT },
    jg: { items: ['agent'] },
    mw: { items: ['mw_agent_script'], note: 'On camera, with a script and coaching.' },
  },
  ao_reel_captions: {
    vt: null, jg: null, mw: null, none: { mw: 'Captions come with their cinematic reel.', vt: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_reel_ramps: {
    vt: null, jg: null, mw: null, none: { mw: 'Speed ramps come with their cinematic reel.', vt: 'Not on their form.', jg: 'Pacing is a free choice on their form.' },
  },
  ao_reel_timelapse: {
    vt: null, jg: null, mw: null, none: { mw: 'Timelapse only comes in their Signature film.', vt: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_simple_extra: {
    vt: { items: [{ id: 'vt_vertical_reel', perLine: true }], note: 'Each extra is another full reel with them.' },
    jg: null, none: { jg: 'Their form takes one of each reel per order.' },
    mw: { items: ['mw_reel_booster'], note: 'Four extra social cuts in one pack; not sold singly.' },
  },

  // ── RotoTour options ──
  ao_tour_both: {
    vt: { includedIn: 'vt_fpv_tour', note: 'Their FPV tour ships both formats.' },
    jg: null, none: { jg: JG_NO_FPV },
    mw: { includedIn: 'mw_fpv_tour', note: 'Their FPV tour ships horizontal and vertical.' },
  },
  ao_tour_teaser: {
    vt: { includedIn: 'vt_fpv_tour', note: 'Their FPV tour ships a vertical reel.' },
    jg: null, none: { jg: JG_NO_FPV },
    mw: { includedIn: 'mw_fpv_tour', note: 'Their FPV tour ships two short reels.' },
  },

  // ── Floor plan options ──
  ao_fp3d: {
    vt: { items: ['vt_add_3d_floor_plan'] },
    jg: null, none: { jg: 'No 3D plan on their form.' },
    mw: { items: ['mw_fp_3d'] },
  },
  ao_fpsameday: {
    vt: null, jg: null, mw: null, none: { mw: 'Not on their form.', vt: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_fpansi: {
    vt: null, jg: null, mw: null, none: { mw: 'Not on their form.', vt: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_fpadu: {
    vt: { items: ['vt_adu_floor_plan'], note: 'Their ADU plan sold on its own; a $50 one only comes inside their Essential Package.' },
    jg: null, mw: null, none: { mw: 'Not on their form.', jg: 'No separate ADU plan on their form; their 2D Floor Plan is one flat $100.' },
  },

  // ── Any shoot ──
  ao_website: {
    vt: { items: ['vt_website_custom_domain'] },
    jg: null, mw: null, none: { mw: 'Their property website only comes in their packages; a custom domain is $50.', jg: 'They build listing sites but don’t price them on their form.' },
  },

  // ── Old list only ──
  ao_branded_new: {
    vt: { items: ['vt_branded_version'], includedWith: ['vt_cinematic_video', 'vt_luxury_video'] },
    jg: null, none: { jg: 'Not on their form.' },
    mw: { items: [], includedWith: MW_VIDEOS, note: 'Their videos ship branded and unbranded.' },
  },
  ao_branded_ret: {
    vt: { items: ['vt_branded_version'], includedWith: ['vt_cinematic_video', 'vt_luxury_video'] },
    jg: null, none: { jg: 'Not on their form.' },
    mw: { items: [], includedWith: MW_VIDEOS, note: 'Their videos ship branded and unbranded.' },
  },
  ao_3d_single: {
    vt: null, jg: null, mw: null, none: { mw: 'Not on their form.', vt: 'Not on their form.', jg: JG_NO_FPV },
  },
  ao_3d_both: {
    vt: null, jg: null, mw: null, none: { mw: 'Not on their form.', vt: 'Not on their form.', jg: JG_NO_FPV },
  },
  ao_d2n_single: {
    vt: { items: ['vt_twilight_video'] },
    jg: null, none: { jg: JG_D2N },
    mw: { items: ['mw_twilight_video'], note: 'Twilight footage cut into their film.' },
  },
  ao_d2n_both: {
    vt: { items: ['vt_twilight_video'] },
    jg: null, none: { jg: JG_D2N },
    mw: { items: ['mw_twilight_video'], note: 'Twilight footage cut into their film.' },
  },
  ao_vidlength: {
    vt: { items: ['vt_upgrade_highlight_cinematic'], note: VT_PACKAGE_ONLY },
    jg: null, mw: null, none: { mw: 'Not on their form.', jg: 'Not on their form.' },
  },
  ao_luxury: {
    vt: { items: ['vt_upgrade_highlight_luxury'], note: VT_PACKAGE_ONLY },
    jg: null, mw: null, none: { mw: 'Not on their form.', jg: 'Not on their form.' },
  },
};
